import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { Server } from '../api/types.js';
import { useServers } from './use-servers.js';

// The server the menu shows (operator, 30/10/2026): the one whose page is open, or To do's server filter, else the
// last one shown on this device, else the first. Remembered per device only, as the sidebar's fold is.

const KEY = 'aidev-current-server';

const remembered = (): string | null => { try { return localStorage.getItem(KEY); } catch { return null; } };

export function serverIdOfPath(pathname: string): string | null {
  const m = /^\/servers\/([^/]+)/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}

export function useCurrentServer(): { server: Server | null; servers: Server[] } {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { data } = useServers();
  const servers = data ?? [];
  const wanted = serverIdOfPath(pathname) ?? (pathname === '/' ? params.get('server') : null) ?? remembered();
  const server = servers.find((s) => s.id === wanted) ?? servers[0] ?? null;
  useEffect(() => {
    if (!server) return;
    try { localStorage.setItem(KEY, server.id); } catch { /* the next visit starts on the first server */ }
  }, [server]);
  return { server, servers };
}

/** The same page on another server: its tab, not a session or a ticket of the first one */
export function samePageOn(pathname: string, from: string, to: string): string {
  const base = `/servers/${encodeURIComponent(from)}`;
  if (pathname !== base && !pathname.startsWith(`${base}/`)) return `/servers/${encodeURIComponent(to)}`;
  const page = pathname.slice(base.length).split('/')[1] ?? '';
  const keeps = ['reviews', 'people', 'sprint', 'mail', 'meetings', 'files', 'settings'];
  return `/servers/${encodeURIComponent(to)}${keeps.includes(page) ? `/${page}` : ''}`;
}
